import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { cn } from '@shared/lib';
import { PlainField } from '@shared/ui';
import type { Level, ModuleSummary } from '@shared/types';
import type { SaveManualCardArgs } from '../api/workspaceApi';

/** Длина перевода ручной карточки (совпадает с TranslationStr на бэке). */
export const MAX_TRANSLATION_CHARS = 200;

// ============================================================
// Icons
// ============================================================
function PlusIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      className={className}
    >
      <path d="M8 3v10M3 8h10" />
    </svg>
  );
}
function SparkIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" className={className}>
      <path d="M9 1L3 9h4l-1 6 6-9H8z" />
    </svg>
  );
}
function EditIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M11 2l3 3-9 9H2v-3z" />
    </svg>
  );
}

// ============================================================
// CreationMode = режим композера: AI или ручной
// ============================================================
export type CreationMode = 'ai' | 'manual';

interface CreationModeTabsProps {
  mode: CreationMode;
  onChange: (m: CreationMode) => void;
  className?: string;
  /** Переопределение подписей (например, компактные «С AI» в мобильной панели ввода). */
  labels?: Partial<Record<CreationMode, string>>;
  /** inline — компактный сегмент внутри поля ввода (макет «Создание карточек»);
   *  активная вкладка оранжевая в обоих режимах. */
  variant?: 'default' | 'inline';
}

const MODE_OPTIONS: ReadonlyArray<{
  key: CreationMode;
  label: string;
  short: string;
  Icon: typeof SparkIcon;
}> = [
  { key: 'ai', label: 'Из словаря', short: 'Из словаря', Icon: SparkIcon },
  { key: 'manual', label: 'Вручную', short: 'Вручную', Icon: EditIcon },
];

export function CreationModeTabs({
  mode,
  onChange,
  className,
  labels,
  variant = 'default',
}: CreationModeTabsProps) {
  const inline = variant === 'inline';
  return (
    <div
      className={cn(
        'inline-flex max-w-full items-center rounded-pill border border-line',
        inline ? 'gap-[3px] bg-base p-[3px]' : 'gap-1 bg-card p-1',
        className,
      )}
    >
      {MODE_OPTIONS.map(({ key, label: defaultLabel, short, Icon }) => {
        const label = labels?.[key] ?? (inline ? short : defaultLabel);
        const active = mode === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={cn(
              // «Из словаря» длиннее прежнего «С AI»: на телефоне без иконки и с меньшими полями,
              // иначе подпись переносилась на две строки, а «Скрыть» уезжало за край (2026-10-16).
              'relative inline-flex items-center gap-2 whitespace-nowrap rounded-pill text-[14px] transition-colors',
              inline ? 'h-9 px-3.5 font-semibold' : 'px-3 py-2 font-medium sm:px-4',
              active ? 'text-fg-inv' : 'text-muted hover:text-fg',
            )}
          >
            {active && (
              <motion.span
                layoutId={inline ? 'creation-mode-pill-inline' : 'creation-mode-pill'}
                className="absolute inset-0 rounded-pill bg-accent shadow-[0_4px_12px_-4px_rgba(243,106,28,0.4)]"
                transition={{ type: 'spring', stiffness: 500, damping: 38 }}
              />
            )}
            <span className="relative z-10 inline-flex items-center gap-2">
              <Icon className={cn('h-3 w-3', !inline && 'hidden sm:block')} />
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ============================================================
// ManualComposer — два инпута «слово + перевод»; словарь даёт
// кликабельные подсказки: клик подставляет перевод во второй инпут.
// ============================================================
interface ManualComposerProps {
  /** Текущий модуль: выбирается в строке заголовка страницы. */
  module: ModuleSummary | null;
  moduleId: string | null;
  isSubmitting: boolean;
  lastError: string | null;
  onClearError: () => void;
  onSubmit: (args: SaveManualCardArgs) => Promise<void>;
  /** Предзаполнить слово (приходим из AI-режима, когда слова нет в словаре). */
  seedFront?: string;
  /** Переключатель AI / вручную — в правом верхнем углу поля. */
  modeSwitch?: ReactNode;
}
export function ManualComposer({
  module,
  moduleId,
  isSubmitting,
  lastError,
  onClearError,
  onSubmit,
  seedFront,
  modeSwitch,
}: ManualComposerProps) {
  const { t } = useTranslation();
  const [front, setFront] = useState('');
  const [back, setBack] = useState('');
  // Ручная карточка идёт без уровня сложности — пилюли CEFR убраны из UI.
  // Бэкенду нужно непустое значение, поэтому посылаем дефолтный B1.
  const cefr: Level = 'B1';
  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);

  // Ручная карточка — только «слово → свой перевод»: поиска по словарю здесь нет
  // (решение 2026-09-25). Транскрипцию и часть речи бэк всё равно подтянет сам.
  useEffect(() => {
    frontRef.current?.focus();
  }, []);

  // Пришли из AI-режима «слова нет в словаре» — подставляем слово и сразу ведём
  // в поле своего перевода.
  useEffect(() => {
    if (seedFront && seedFront.trim()) {
      setFront(seedFront);
      requestAnimationFrame(() => backRef.current?.focus());
    }
  }, [seedFront]);

  const valid = front.trim().length >= 1 && back.trim().length >= 1 && moduleId != null;

  const submit = async () => {
    if (!valid || isSubmitting) return;
    try {
      await onSubmit({
        front: front.trim(),
        // Ровно один перевод: словарь — только подсказка, учится введённое во 2-й инпут.
        translations: [back.trim()],
        moduleId: moduleId!,
        moduleTitle: module?.title ?? '',
        difficulty: cefr,
      });
      setFront('');
      setBack('');
      frontRef.current?.focus();
    } catch {
      // ошибка показывается через lastError
    }
  };

  return (
    <div onClick={() => lastError && onClearError()} className="cw-composer mb-6">
      <div className="rounded-card border border-line bg-card">
        {modeSwitch && (
          <div className="flex items-center justify-between gap-3 pl-4 pr-2.5 pt-2.5">
            <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-muted">
              {t('cardsWorkspace.manual.boxTitle')}
            </span>
            {modeSwitch}
          </div>
        )}
        <div className="cw-composer-shell">
          <div className="cw-composer-fields">
            <label className="flex items-center gap-2 rounded-xl border border-line bg-elev/60 px-3 py-1.5 transition-colors focus-within:border-accent focus-within:bg-elev focus-within:shadow-[0_0_0_3px_rgba(243,106,28,0.12)]">
              <span className="shrink-0 text-[12px] font-semibold uppercase tracking-[0.14em] text-muted-2">
                {t('cardsWorkspace.manual.wordLabel')}
              </span>
              <PlainField
                ref={frontRef}
                value={front}
                onChange={setFront}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    // Перевод уже подставлен/введён → создаём; иначе ведём в поле перевода.
                    if (back.trim()) void submit();
                    else backRef.current?.focus();
                  }
                  if (e.key === 'Tab' && !e.shiftKey) {
                    e.preventDefault();
                    backRef.current?.focus();
                  }
                }}
                placeholder={t('cardsWorkspace.manual.wordPlaceholder')}
                ariaLabel={t('cardsWorkspace.manual.wordLabel')}
                className="w-full bg-transparent py-1.5 font-serif text-[15px] font-medium uppercase tracking-[0.02em] text-fg outline-none sm:text-[16px]"
                autoCapitalize="characters"
                spellCheck={false}
              />
            </label>

            <label className="flex items-center gap-2 rounded-xl border border-line bg-elev/60 px-3 py-1.5 transition-colors focus-within:border-accent focus-within:bg-elev focus-within:shadow-[0_0_0_3px_rgba(243,106,28,0.12)]">
              <span className="shrink-0 text-[12px] font-semibold uppercase tracking-[0.14em] text-muted-2">
                {t('cardsWorkspace.manual.translationLabel')}
              </span>
              <PlainField
                ref={backRef}
                value={back}
                onChange={(v) => setBack(v.slice(0, MAX_TRANSLATION_CHARS))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void submit();
                  }
                }}
                placeholder={t('cardsWorkspace.manual.translationPlaceholder')}
                ariaLabel={t('cardsWorkspace.manual.translationLabel')}
                className="w-full bg-transparent py-1.5 font-serif text-[15px] font-medium text-fg outline-none sm:text-[16px]"
                spellCheck={false}
              />
            </label>
          </div>

          <div className="cw-composer-tail">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!valid || isSubmitting}
              className="cw-composer-create inline-flex items-center justify-center gap-1.5 rounded-pill bg-accent px-4 py-2.5 text-[14px] font-semibold text-fg-inv transition-transform hover:-translate-y-0.5 hover:bg-accent-hi disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              {isSubmitting
                ? t('cardsWorkspace.manual.submitting')
                : t('cardsWorkspace.manual.submit')}
            </button>
          </div>
        </div>

        {/* Чем ручная карточка беднее AI — одной строкой внутри поля (решение владельца 2026-10-06). */}
        <div className="flex items-start gap-2 border-t border-line/70 px-3.5 py-2.5 text-[12px] leading-[1.45]">
          <span className="mt-px grid h-4 w-4 shrink-0 place-items-center rounded-full bg-warn/20 text-[12px] font-bold text-warn">
            i
          </span>
          <span className="text-muted">
            <span className="font-medium text-fg">{t('cardsWorkspace.manual.limitTitle')}</span>{' '}
            {t('cardsWorkspace.manual.limitBody')}
          </span>
        </div>
      </div>

      {lastError && (
        <div className="mt-2 px-1 text-[12px] font-medium text-danger">{lastError}</div>
      )}
    </div>
  );
}
